import { useCallback, useReducer } from 'react';
import type { Dispatch, SetStateAction } from 'react';

const HISTORY_LIMIT = 100;

export interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
  batching: boolean;
  batchStart: T | null;
}

export type HistoryAction<T> =
  | { type: 'set'; value: SetStateAction<T> }
  | { type: 'reset'; value: T }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'begin-batch' }
  | { type: 'end-batch' };

export function createHistoryState<T>(value: T): HistoryState<T> {
  return { past: [], present: value, future: [], batching: false, batchStart: null };
}

export function historyReducer<T>(
  state: HistoryState<T>,
  action: HistoryAction<T>,
): HistoryState<T> {
  if (action.type === 'set') {
    const next =
      typeof action.value === 'function'
        ? (action.value as (current: T) => T)(state.present)
        : action.value;
    if (historyValuesEqual(next, state.present)) return state;
    if (state.batching) return { ...state, present: next, future: [] };
    return {
      past: appendWithLimit(state.past, state.present),
      present: next,
      future: [],
      batching: false,
      batchStart: null,
    };
  }

  if (action.type === 'reset') return createHistoryState(action.value);

  if (action.type === 'begin-batch') {
    if (state.batching) return state;
    return { ...state, batching: true, batchStart: state.present };
  }

  if (action.type === 'end-batch') {
    if (!state.batching) return state;
    if (state.batchStart === null || Object.is(state.batchStart, state.present)) {
      return { ...state, batching: false, batchStart: null };
    }
    return {
      ...state,
      past: appendWithLimit(state.past, state.batchStart),
      batching: false,
      batchStart: null,
    };
  }

  if (action.type === 'undo') {
    if (state.past.length === 0) return state;
    const previous = state.past[state.past.length - 1]!;
    return {
      past: state.past.slice(0, -1),
      present: previous,
      future: [state.present, ...state.future],
      batching: false,
      batchStart: null,
    };
  }

  if (state.future.length === 0) return state;
  const next = state.future[0]!;
  return {
    past: appendWithLimit(state.past, state.present),
    present: next,
    future: state.future.slice(1),
    batching: false,
    batchStart: null,
  };
}

export function useUndoableState<T>(initialValue: T) {
  const [history, dispatch] = useReducer(historyReducer<T>, initialValue, createHistoryState<T>);
  const set = useCallback<Dispatch<SetStateAction<T>>>((value) => {
    dispatch({ type: 'set', value });
  }, []);
  const reset = useCallback((value: T) => dispatch({ type: 'reset', value }), []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const beginBatch = useCallback(() => dispatch({ type: 'begin-batch' }), []);
  const endBatch = useCallback(() => dispatch({ type: 'end-batch' }), []);

  return {
    value: history.present,
    set,
    reset,
    undo,
    redo,
    beginBatch,
    endBatch,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  };
}

function appendWithLimit<T>(items: T[], value: T): T[] {
  const next = [...items, value];
  return next.length > HISTORY_LIMIT ? next.slice(next.length - HISTORY_LIMIT) : next;
}

function historyValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object') {
    return false;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((value, index) => historyValuesEqual(value, right[index]));
  }
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord);
  const rightKeys = Object.keys(rightRecord);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(rightRecord, key) &&
      historyValuesEqual(leftRecord[key], rightRecord[key]),
  );
}
